import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { type Project, type Share, type Space, shareIsLive } from "@scenix/core";
import { ApiError, api } from "../lib/api";

interface SharePanelProps {
  spaceId: Space["id"];
  projectId: Project["id"];
  /** Minting a link gives access away, so it is Editor-only on the API. */
  mayEdit: boolean;
}

/**
 * How long a new link lasts, in the days the wire carries (ADR 000133).
 *
 * Thirty is where the form starts. The review round a share exists for is days
 * to a fortnight, and past a month the link is no longer being used for the
 * thing it was made for while its holder is whoever the mail thread reached.
 * Never is still offered, because the long review ADR 000057 describes is real
 * and a link for it should not die halfway through.
 */
const LIFETIMES = [
  { days: 7, label: "7 days" },
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
  // Zero is how NewShare says "does not expire", not a lifetime of no days.
  { days: 0, label: "Never" },
] as const;

const DEFAULT_LIFETIME_DAYS = 30;

/**
 * Links to this project for people who are not in the space (ADR 000057).
 *
 * A share is read-only and shows one project: the cut, how much of it is made,
 * and nothing else. Not the corpus, not the thread, not another project.
 *
 * The token appears once, here, at the moment it is minted, and never again.
 * A listing that carried working links would make every reader of it an
 * audience, so what a listing carries is a label and the tail of the token.
 */
export function SharePanel({ spaceId, projectId, mayEdit }: SharePanelProps) {
  const queryClient = useQueryClient();
  const [label, setLabel] = useState("");
  const [lifetimeDays, setLifetimeDays] = useState<number>(DEFAULT_LIFETIME_DAYS);
  const [minted, setMinted] = useState<{
    label: string;
    url: string;
    expiresAt: string | null;
  } | null>(null);
  const [copied, setCopied] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const sharesKey = ["shares", spaceId, projectId];

  const shares = useQuery({
    queryKey: sharesKey,
    queryFn: () => api.listShares(spaceId, projectId),
    // A viewer is refused this, so asking would be a 403 on every render.
    enabled: mayEdit,
  });

  const create = useMutation({
    mutationFn: ({ forWhom, days }: { forWhom: string; days: number }) =>
      api.createShare(spaceId, projectId, { label: forWhom, expiresInDays: days }),
    onSuccess: (created) => {
      setFailure(null);
      setLabel("");
      setCopied(false);
      // Built here rather than returned by the API: the link's shape is the
      // studio's to decide, and the server has no business knowing where this
      // is deployed.
      setMinted({
        label: created.share.label,
        url: `${window.location.origin}/shared/${created.token}`,
        expiresAt: created.share.expiresAt ?? null,
      });
      void queryClient.invalidateQueries({ queryKey: sharesKey });
    },
    onError: (error: unknown) => setFailure(describe(error)),
  });

  const revoke = useMutation({
    mutationFn: (shareId: Share["id"]) => api.revokeShare(spaceId, projectId, shareId),
    onSuccess: () => {
      setFailure(null);
      void queryClient.invalidateQueries({ queryKey: sharesKey });
    },
    onError: (error: unknown) => setFailure(describe(error)),
  });

  if (!mayEdit) return null;

  const live = (shares.data ?? []).filter((share) => shareIsLive(share));

  return (
    <section className="share-panel">
      <h2 className="section-head">Share this cut</h2>

      {failure && (
        <p role="alert" className="error">
          {failure}
        </p>
      )}

      <form
        className="row"
        onSubmit={(event) => {
          event.preventDefault();
          if (label.trim()) create.mutate({ forWhom: label.trim(), days: lifetimeDays });
        }}
      >
        <label>
          <span className="visually-hidden">Who is this link for</span>
          <input
            value={label}
            placeholder="Who is this for? e.g. the client"
            onChange={(event) => setLabel(event.target.value)}
          />
        </label>
        <select
          aria-label="How long the link lasts"
          value={lifetimeDays}
          // Left on whatever was chosen, because minting a second link for the
          // same round wants the same lifetime as the first.
          onChange={(event) => setLifetimeDays(Number(event.target.value))}
        >
          {LIFETIMES.map((lifetime) => (
            <option key={lifetime.days} value={lifetime.days}>
              {lifetime.label}
            </option>
          ))}
        </select>
        <button type="submit" disabled={create.isPending || label.trim() === ""}>
          Make a link
        </button>
      </form>

      {minted && (
        <div className="share-minted">
          <p>
            The link for <strong>{minted.label}</strong>. This is the only time it is shown.
          </p>
          <div className="row">
            <input readOnly value={minted.url} aria-label="The share link" />
            <button
              type="button"
              onClick={() => {
                void navigator.clipboard?.writeText(minted.url);
                setCopied(true);
              }}
            >
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
          <p className="muted">
            Anybody with this link can watch the cut without signing in. They cannot see the rest of
            the space, and they cannot change anything.{" "}
            {minted.expiresAt
              ? `It stops working on ${new Date(minted.expiresAt).toLocaleDateString()}.`
              : "It never expires, so revoking it is the only way to stop it."}
          </p>
        </div>
      )}

      {shares.isPending ? (
        <p className="muted">Reading the links…</p>
      ) : live.length === 0 ? (
        <p className="muted">
          No links yet. A link lets somebody outside the space watch this one project, and nothing
          else.
        </p>
      ) : (
        <ul className="share-list">
          {live.map((share) => (
            <li key={share.id}>
              <span className="share-body">
                <strong>{share.label}</strong>
                <span className="muted">
                  ends …{share.hint}
                  {share.expiresAt
                    ? ` · expires ${new Date(share.expiresAt).toLocaleDateString()}`
                    : ""}
                  {share.lastUsedAt
                    ? ` · opened ${new Date(share.lastUsedAt).toLocaleDateString()}`
                    : " · never opened"}
                </span>
              </span>
              <button
                type="button"
                className="ghost danger"
                disabled={revoke.isPending}
                onClick={() => {
                  if (window.confirm(`Stop the link for "${share.label}" working?`)) {
                    revoke.mutate(share.id);
                  }
                }}
              >
                Revoke
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function describe(error: unknown): string {
  if (error instanceof ApiError) return error.userMessage;
  return error instanceof Error ? error.message : "Something went wrong.";
}
